/**
 * What this phone knows about the turn, held across the cut from reveal to
 * justification.
 *
 * Those are two routes driven by the server's phase, but one screen to the
 * player (DESIGN.md §3.6): same ranking, same rating bar, same End turn. Two
 * things would otherwise change under them at the cut.
 *
 * 1. The store nulls `reveal` as `phase_changed` goes past — deliberately,
 *    because a phone that reconnects mid-argument never saw one and must not
 *    render a half-remembered list (SPEC §5.2). The phones that *were* watching
 *    would lose their attribution chips and their score line.
 * 2. The route changes, so component state goes with it: a thumb pressed during
 *    the reveal would un-press itself a few seconds later.
 *
 * Nothing is computed here. It is the server's own payload plus this player's
 * own two intents, in memory, keyed to the turn they belong to, dropped the
 * moment another turn's reveal lands, and never written to storage of any kind
 * (SPEC §6).
 */
import type { RevealPayload, ThumbsVote } from '@idgame/protocol';

/**
 * Turn index alone repeats after "play again"; question numbers stay burned for
 * the life of the room, so the pair cannot collide across games.
 */
export function turnKey(turnIndex: number, questionNumber: number): string {
  return `${turnIndex}:${questionNumber}`;
}

interface TurnMemory {
  key: string;
  reveal: RevealPayload | null;
  vote: ThumbsVote | null;
  endRequested: boolean;
  /**
   * The server refuses both `thumbs_vote` and `justification_done` while the
   * reveal is still running, so an intent formed there is unsent until the
   * phase lands.
   */
  pending: { vote: boolean; end: boolean };
}

let memory: TurnMemory | null = null;

function open(key: string): TurnMemory {
  if (!memory || memory.key !== key) {
    memory = { key, reveal: null, vote: null, endRequested: false, pending: { vote: false, end: false } };
  }
  return memory;
}

function read(key: string): TurnMemory | null {
  return memory && memory.key === key ? memory : null;
}

export function rememberReveal(reveal: RevealPayload): void {
  open(turnKey(reveal.turnIndex, reveal.question.number)).reveal = reveal;
}

export function recallReveal(turnIndex: number, questionNumber: number): RevealPayload | null {
  return read(turnKey(turnIndex, questionNumber))?.reveal ?? null;
}

export interface TurnIntents {
  vote: ThumbsVote | null;
  endRequested: boolean;
}

export function recallIntents(key: string): TurnIntents {
  const held = read(key);
  return { vote: held?.vote ?? null, endRequested: held?.endRequested ?? false };
}

/** Records the intent. `sent` is false when the server would refuse it yet. */
export function recordVote(key: string, vote: ThumbsVote, sent: boolean): void {
  const held = open(key);
  held.vote = vote;
  held.pending.vote = !sent;
}

export function recordEndTurn(key: string, sent: boolean): void {
  const held = open(key);
  held.endRequested = true;
  held.pending.end = !sent;
}

/**
 * Everything the player asked for while the server was not listening, cleared
 * as it is handed over so each intent can only be sent once.
 */
export function drainPending(key: string): { vote: ThumbsVote | null; end: boolean } {
  const held = read(key);
  if (!held) return { vote: null, end: false };
  const out = { vote: held.pending.vote ? held.vote : null, end: held.pending.end };
  held.pending = { vote: false, end: false };
  return out;
}

/** Tests only — the module outlives a single render tree by design. */
export function forgetTurn(): void {
  memory = null;
}
